// src/stores/user.ts
import { defineStore } from 'pinia';
import {
  AuthUser,
  KCCredentialDescriptor,
  KCCredentialsActions,
} from '@/types/auth-user';
import keycloak from '@/auth/keycloak';
import {
  UserAccountDocument,
  UserSignedInDocument,
  UserSignedOutDocument,
  OrganizationUserAssociationType,
} from '@/gql/graphql';
import apolloClient from '@/apollo/client';

let refreshTimeout: number | undefined;

export const useUserStore = defineStore('user', {
  state: (): AuthUser => ({
    id: null,
    uuid: null, // Permanent user ID from the `sub` field of the token
    email: null,
    roles: [],
    token: null, // To keep the parsed token if needed
    jwt: null, // Bearer token for dev and debugging purposes
    authHeader: null,
    authenticated: false,
    admin: false,
    developer: false,
    activeOrgAssociation: null,
    mfa: [],
    pendingInvites: 0, // Number of pending invites for the user
  }),
  getters: {
    isAuthenticated: (state) => state.authenticated,
    isAdmin: (state) => state.admin,
    isDeveloper: (state) => state.developer,
    noOrganizationSelected: (state) => state.activeOrgAssociation === null,
    mfaNotConfigured: (state) =>
      !state.mfa.some((item) => item.includes('2fa')),
  },
  actions: {
    async initKeycloak() {
      if (__CA_DEV_NO_LOGIN__) {
        const { signInDevUser } = await import('@/auth/devNoLogin');
        return signInDevUser(this);
      }
      const auth = await keycloak.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri:
          window.location.origin + '/silent-check-sso.html',
      });
      this.authenticated = auth;
      if (auth) {
        this.uuid = keycloak.tokenParsed.sub;
        this.email = keycloak.tokenParsed.email;
        this.roles =
          keycloak.tokenParsed.resource_access[
            process.env.VUE_APP_KEYCLOAK_CLIENT_ID
          ]?.roles || [];
        this.token = keycloak.tokenParsed;
        this.jwt = keycloak.token;
        this.authHeader = `{"Authorization": "Bearer ${keycloak.token}"}`;
        this.admin = this.roles.indexOf('admin') >= 0;
        this.developer = this.roles.indexOf('developer') >= 0;
        this.mfa = keycloak.tokenParsed.amr || [];

        // Set up token expiration handler
        keycloak.onTokenExpired = async () => {
          try {
            // refresh immediately when KC says it's expired
            await keycloak.updateToken(30);
            // update store fields so UI reflects the new token (optional)
            this.token = keycloak.tokenParsed;
            this.jwt = keycloak.token;
            this.authHeader = `{"Authorization": "Bearer ${keycloak.token}"}`;
          } catch (e) {
            // can't refresh → force re-login
            keycloak.login();
          }
        };

        // refresh when tab becomes visible or comes back online
        document.addEventListener('visibilitychange', async () => {
          if (document.visibilityState === 'visible') {
            try {
              await keycloak.updateToken(60);
            } catch {
              keycloak.login();
            }
          }
        });
        window.addEventListener('online', async () => {
          try {
            await keycloak.updateToken(60);
          } catch {
            keycloak.login();
          }
        });

        // Check if we've already logged the initial login in this session.
        await this.logSignIn();

        // Fetch user details if ID or active organization association is missing.
        if (!this.id || !this.activeOrgAssociation) {
          await this.fetchUserDetails();
        }

        // Set up onAuthSuccess to handle any subsequent refresh events.
        keycloak.onAuthSuccess = () => {
          this.logSignIn();
        };
      }
    },
    async fetchUserCredentials(): Promise<KCCredentialDescriptor[]> {
      if (__CA_DEV_NO_LOGIN__) return []; // no Keycloak account to read
      const r = await fetch(
        `${process.env.VUE_APP_KEYCLOAK_URL}/realms/${process.env.VUE_APP_KEYCLOAK_REALM}/account/credentials`,
        {
          headers: {
            Authorization: `Bearer ${keycloak.token}`,
            Accept: 'application/json',
          },
        }
      );

      if (!r.ok) {
        throw new Error(`Error obtaining credentials: ${r.statusText}`);
      }

      const creds: KCCredentialDescriptor[] = await r.json();
      console.debug('Raw credentials: ', creds);

      // save the credetials to user store for reference
      this.mfa = creds
        .filter((cred) => cred.userCredentialMetadatas.length > 0)
        .map(
          (cred) =>
            cred.type +
            '-' +
            cred.category
              .replace('two-factor', '2fa')
              .replace('basic-authentication', '1fa')
        );
      console.debug('User MFA methods:', this.mfa);

      // return credentials for further processing
      return creds;
    },
    configureMfa(action: KCCredentialsActions) {
      window.location.href =
        process.env.VUE_APP_KEYCLOAK_URL +
        '/realms/' +
        process.env.VUE_APP_KEYCLOAK_REALM +
        '/protocol/openid-connect/auth?client_id=' +
        process.env.VUE_APP_KEYCLOAK_CLIENT_ID +
        '&redirect_uri=' +
        process.env.VUE_APP_LOGOUT_REDIRECT_URL +
        '&response_type=code&scope=openid&kc_action=' +
        action;
    },
    async credentialDelete(credentialId: string): Promise<boolean> {
      const r = await fetch(
        `${process.env.VUE_APP_KEYCLOAK_URL}/realms/${process.env.VUE_APP_KEYCLOAK_REALM}/account/credentials/${credentialId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${keycloak.token}`,
            Accept: 'application/json',
          },
        }
      );

      if (!r.ok) {
        throw new Error(`Delete failed: ${r.statusText}`);
      }

      return true;
    },
    async fetchUserDetails() {
      // Only fetch if user ID is not already set
      if (this.id) {
        console.debug('User ID already available, skipping fetch');
        return;
      }

      //Start Timer
      const startTime = performance.now();
      console.debug('User ID not available, fetching user details');

      // Fetch user details via GraphQL
      await apolloClient
        .query({
          query: UserAccountDocument,
        })
        .then((response) => {
          if (response.data?.user) {
            console.debug('User details fetched:', response.data?.user);
            // assign user id if available
            if (response.data.user.id) {
              console.debug('User ID', response.data.user.id, 'assigned');
              this.id = response.data.user.id;
            }
            // assign active organization association if available
            this.setActiveOrganization(
              response.data.user.organizationsAssociated || null,
              response.data.user.activeOrganizationId || null
            );
            // assign pending invites count if available
            if (response.data.user.invitationsReceived) {
              this.setNumberOfPendingInvites(
                response.data.user.invitationsReceived.length
              );
            }
          } else {
            console.error('Error fetching user details:', response.errors);
          }
        })
        .catch((error) => {
          console.error('Error fetching user details:', error);
        })
        .finally(() => {
          //End Timer
          const endTime = performance.now();
          const duration = endTime - startTime;
          // Estimate response size by the lenght of the JSON stringified data
          const responseSize = JSON.stringify(
            apolloClient.cache.extract()
          ).length;
          // Log performance metrics
          console.debug(
            `Initial user details fetch: ${responseSize / 1024 / 1024} MB / ${
              duration / 1000
            } seconds`
          );
        });
    },
    setActiveOrganization(
      organizationsAssociated:
        | (OrganizationUserAssociationType | null)[]
        | null,
      activeOrganizationId: number | null
    ) {
      this.activeOrgAssociation =
        organizationsAssociated?.find(
          (assoc) =>
            assoc?.organization?.id === activeOrganizationId &&
            assoc?.removedAt == null
        ) || null;
    },
    setNumberOfPendingInvites(count: number) {
      this.pendingInvites = count;
    },
    async logSignIn() {
      // Check if the sign-in action has already been logged in this session
      if (sessionStorage.getItem('signInActionLogged')) {
        console.debug('User already signed in, no need to log again');
        return;
      }
      // Ensure there is a valid user UUID
      if (!this.uuid) {
        console.warn('User UUID not available, cannot log sign-in action.');
        return;
      }
      // Call mutation to log the sing-in action and fetch the user details
      console.debug('User just signed in, logging the user sign-in action');
      await apolloClient
        .mutate({
          mutation: UserSignedInDocument,
          variables: { mfa: this.mfa },
        })
        .then((response) => {
          if (response.data?.userSignedIn) {
            console.debug('User sign-in logged:', response.data?.userSignedIn);
            // assign user id if available
            if (response.data.userSignedIn.id) {
              console.debug(
                'User ID',
                response.data.userSignedIn.id,
                'fetched and assigned successfuly'
              );
              this.id = response.data.userSignedIn.id;
            }
            // assign active organization association if available
            this.setActiveOrganization(
              response.data.userSignedIn.organizationsAssociated || null,
              response.data.userSignedIn.activeOrganizationId || null
            );
            console.debug(
              'User active organization association set: ',
              this.activeOrgAssociation
            );
            // assign pending invites count if available
            if (response.data.userSignedIn.invitationsReceived) {
              this.setNumberOfPendingInvites(
                response.data.userSignedIn.invitationsReceived.length
              );
              console.debug(
                'User pending invites count set: ',
                this.pendingInvites
              );
            }
          } else {
            console.error('Error logging user sign-in:', response.errors);
          }
        })
        .catch((error) => {
          console.error('Error logging user sign-in:', error);
        });
      // Set the sign-in action logged flag
      sessionStorage.setItem('signInActionLogged', 'true');
      // User sing-in action logged successfuly
      console.debug('User log sign-in action complete.');
    },
    async logSignOut() {
      // Ensure there is a valid user UUID
      if (!this.uuid) {
        console.warn('User UUID not available, cannot log sign-out action');
        return;
      }
      // Call mutation to log the sing-in action and fetch the user details
      await apolloClient
        .mutate({
          mutation: UserSignedOutDocument,
        })
        .then((response) => {
          console.log('response', response);
          if (response.data?.userSignedOut) {
            console.debug(
              'User sign-out logged:',
              response.data?.userSignedOut
            );
          } else {
            console.error('Error logging user sign-out:', response.errors);
          }
        })
        .catch((error) => {
          console.error('Error logging user sign-out:', error);
        });
      // User sing-in action logged successfuly
      console.debug('User log sign-out action complete');
    },
    login() {
      keycloak.login();
    },
    async logout() {
      if (__CA_DEV_NO_LOGIN__) return; // the dev user stays signed in
      // Clear the initial login flag so that a future login logs the event again.
      sessionStorage.removeItem('signInActionLogged');

      // Log the sign-out by calling your mutation
      await this.logSignOut();

      // Clear user store
      console.debug('Clearing user store');
      this.id = null;
      this.uuid = null;
      this.email = null;
      this.roles = [];
      this.token = null;
      this.jwt = null;
      this.authHeader = null;
      this.authenticated = false;
      this.admin = false;
      this.developer = false;
      this.mfa = [];

      // Clear the token refresh timeout
      if (refreshTimeout) {
        console.debug('Clearing token refresh timeout');
        clearTimeout(refreshTimeout);
        refreshTimeout = undefined;
      }

      // Execute logout and redirect to the logout URL
      console.debug('Logging out and redirecting to the logout URL');
      const redirectUri =
        (process.env.VUE_APP_LOGOUT_REDIRECT_URL as string) ||
        'https://www.samplinghuman.com';
      keycloak.logout({ redirectUri: redirectUri });
    },
  },
});
